import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { FileText, ImageIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { extractErrorMessage } from '@/lib/api-client';
import { useCreateFueling } from './api';
import { FUEL_TYPE_LABELS } from './types';
import { useVehiclesList } from '@/features/vehicles/api';
import { UploadDropzone } from '@/features/uploads/upload-dropzone';
import type { UploadedFile } from '@/features/uploads/use-file-upload';
import { formatFileSize } from '@/features/documents/types';

const schema = z.object({
  vehicleId: z.string().min(1, 'Selecione o veículo'),
  fuelType: z.string().min(1, 'Selecione o combustível'),
  quantityL: z.coerce.number().positive('Informe a quantidade'),
  pricePerL: z.coerce.number().positive('Informe o preço'),
  totalR: z.coerce.number().positive('Informe o total'),
  odometerKm: z.coerce.number().min(0).optional().or(z.literal('')),
  isFull: z.boolean().default(true),
  fueledAt: z.string().min(1, 'Informe a data'),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof schema>;

interface FuelingFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FuelingFormDialog({ open, onOpenChange }: FuelingFormDialogProps) {
  const create = useCreateFueling();
  const vehiclesQuery = useVehiclesList({ page: 1, limit: 100 });
  const vehicles = vehiclesQuery.data?.data ?? [];
  const [receipt, setReceipt] = useState<UploadedFile | null>(null);

  const form = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      vehicleId: '',
      fuelType: 'flex',
      quantityL: undefined,
      pricePerL: undefined,
      totalR: undefined,
      odometerKm: undefined,
      isFull: true,
      fueledAt: new Date().toISOString().slice(0, 16),
      notes: '',
    },
  });

  // Auto-calc total when qty or price changes
  const qty = form.watch('quantityL');
  const price = form.watch('pricePerL');
  useEffect(() => {
    if (qty && price && qty > 0 && price > 0) {
      const total = Math.round(qty * price * 100) / 100;
      form.setValue('totalR', total, { shouldValidate: false });
    }
  }, [qty, price, form]);

  useEffect(() => {
    if (open) {
      form.reset();
      setReceipt(null);
    }
  }, [open, form]);

  const onSubmit = async (values: FormData) => {
    try {
      await create.mutateAsync({
        vehicleId: values.vehicleId,
        fuelType: values.fuelType,
        quantityMl: Math.round(values.quantityL * 1000),
        priceCentsPerL: Math.round(values.pricePerL * 100),
        totalCents: Math.round(values.totalR * 100),
        odometerMeters: values.odometerKm ? Math.round(Number(values.odometerKm) * 1000) : undefined,
        isFull: values.isFull,
        fueledAt: new Date(values.fueledAt).toISOString(),
        notes: values.notes || undefined,
        receiptUploadId: receipt?.uploadId,
      });
      toast.success('Abastecimento registrado');
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Novo abastecimento</DialogTitle>
          <DialogDescription>Registre um abastecimento manual.</DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4" noValidate>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Veículo *" error={form.formState.errors.vehicleId?.message}>
              <Controller
                control={form.control}
                name="vehicleId"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione..." />
                    </SelectTrigger>
                    <SelectContent>
                      {vehicles.map((v) => (
                        <SelectItem key={v.id} value={v.id}>
                          {v.plate} — {v.brand} {v.model}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
            <Field label="Combustível *" error={form.formState.errors.fuelType?.message}>
              <Controller
                control={form.control}
                name="fuelType"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(FUEL_TYPE_LABELS).map(([val, label]) => (
                        <SelectItem key={val} value={val}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Litros *" error={form.formState.errors.quantityL?.message}>
              <Input type="number" step="0.01" inputMode="decimal" placeholder="50.00" {...form.register('quantityL')} />
            </Field>
            <Field label="R$/L *" error={form.formState.errors.pricePerL?.message}>
              <Input type="number" step="0.01" inputMode="decimal" placeholder="5.89" {...form.register('pricePerL')} />
            </Field>
            <Field label="Total R$ *" error={form.formState.errors.totalR?.message}>
              <Input type="number" step="0.01" inputMode="decimal" {...form.register('totalR')} />
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {(() => {
              const selectedVehicle = vehicles.find(
                (v) => v.id === form.watch('vehicleId'),
              );
              const isHourmeter =
                selectedVehicle?.tracksHourmeter && !selectedVehicle?.tracksOdometer;
              return (
                <Field
                  label={isHourmeter ? 'Horímetro (horas)' : 'Odômetro (km)'}
                  error={form.formState.errors.odometerKm?.message}
                >
                  <Input
                    type="number"
                    inputMode="numeric"
                    placeholder={isHourmeter ? '1200' : '12345'}
                    {...form.register('odometerKm')}
                  />
                </Field>
              );
            })()}
            <Field label="Data/hora *" error={form.formState.errors.fueledAt?.message}>
              <Input type="datetime-local" {...form.register('fueledAt')} />
            </Field>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" className="size-4 accent-[hsl(var(--primary))]" {...form.register('isFull')} />
            Tanque cheio (completou)
          </label>

          <Field label="Observações" error={form.formState.errors.notes?.message}>
            <Input {...form.register('notes')} placeholder="Opcional" />
          </Field>

          {/* Comprovante */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Comprovante (opcional)
            </Label>
            {receipt ? (
              <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/30 p-3">
                <div className="flex size-10 shrink-0 items-center justify-center rounded-md bg-success/15 text-success">
                  {receipt.mimeType === 'application/pdf' ? (
                    <FileText className="size-5" />
                  ) : (
                    <ImageIcon className="size-5" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{receipt.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatFileSize(receipt.sizeBytes)} · Enviado
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8"
                  title="Remover comprovante"
                  onClick={() => setReceipt(null)}
                >
                  <X className="size-4" />
                </Button>
              </div>
            ) : (
              <UploadDropzone
                purpose="fueling_receipt"
                onUploaded={(file) => setReceipt(file)}
                label="Foto ou PDF do cupom fiscal"
              />
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" loading={form.formState.isSubmitting}>Registrar</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {error && <p className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
